import React, {
  useEffect, useState, useContext, useRef, useCallback
} from 'react';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { useStore, ExperienceContext } from '@thd-nucleus/experience-context';
import { useDataModel, useLazyDataModel } from '@thd-nucleus/data-sources';
import { ImpressionProvider } from '@thd-olt-component-react/impression';
import proptypes from 'prop-types';
import className from 'classnames';
import { dynamicRecsDataModel } from '../dataModel';
import {
  isSearchLoading,
  getAppId,
  getLoyaltyMembershipInput,
  impressionContainerNameParser,
  getDrecsImpressionID,
  getDrecsimpressionType
} from '../utils/helpers';
import { ProductPodV7Carousel } from '../core/ProductPodV7Carousel';

const { string, bool, number, shape } = proptypes;

const SearchViewedRecs = ({
  anchorId,
  anchorImage,
  maxItems,
  requestKey,
  showLoading,
  hideFavorites,
  itemIds,
  impressionData,
  name,
  parent,
  zonePosition
}) => {
  const { storeId, storeZip: zipCode, membershipInformation } = useStore();
  const {
    customerType: cType, mcvisID
  } = useThdCustomer() || {};
  const { channel, isConsumerApp } = useContext(ExperienceContext);
  const [data, setData] = useState(null);
  const [searchSchema, setSearchSchema] = useState(null);
  const [impressionName, setImpressionName] = useState('');
  const schemaName = 'searchViewed';
  const multiSchemaName = 'plpsearch_multi';
  const [didFallbackRender, setDidFallbackRender] = useState({ fallbackRendered: false, zoneId: null });
  const listenerSet = useRef(false);
  const fallbackEventHandler = useCallback(({ output: { fallbackRendered, renderedZoneId } }) => {
    setDidFallbackRender({ fallbackRendered, zoneId: renderedZoneId });
  }, []);

  if (typeof LIFE_CYCLE_EVENT_BUS !== 'undefined' && !listenerSet.current) {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.on('discovery-zones.fallback', fallbackEventHandler);
    listenerSet.current = true;
  }

  const searchOpts = {
    variables: {
      anchorId,
      appId: getAppId(channel, isConsumerApp),
      apiName: schemaName,
      dataSource: schemaName,
      loyaltyMembershipInput: getLoyaltyMembershipInput(membershipInformation),
      key: requestKey,
      cType,
      mcvisId: mcvisID,
      timeStamp: null,
      storeId,
      zipCode,
      serviceType: null,
      skipInstallServices: true,
      maxResults: null,
      nvalue: null,
      recsContext: null
    },
    skip: didFallbackRender.fallbackRendered && !zonePosition,
    ssr: false
  };
  const { data: searchData, loading, error, variables } = useDataModel('recs', searchOpts);

  const { recs } = searchData || {};
  const multiOpts = {
    manual: true,
    variables: {
      anchorId: itemIds,
      appId: getAppId(channel, isConsumerApp),
      apiName: multiSchemaName,
      dataSource: multiSchemaName,
      loyaltyMembershipInput: getLoyaltyMembershipInput(membershipInformation),
      key: requestKey,
      serviceType: 'mutlianchor',
      storeId,
      zipCode,
      skipInstallServices: true,
      maxResults: null,
      cType,
      mcvisId: mcvisID,
      timeStamp: null,
      nvalue: null
    },
    ssr: false
  };

  const [
    getMultiProductSkus,
    {
      data: multiData,
      loading: multiLoading,
      error: multiError
    }
  ] = useLazyDataModel('recs', multiOpts);

  const shouldRequestFallback = () => {
    return !loading && (searchData || error) && !multiLoading && !multiError && !multiData && !data;
  };

  useEffect(() => {
    return () => LIFE_CYCLE_EVENT_BUS.lifeCycle.off('discovery-zones.fallback', fallbackEventHandler);
  }, []);

  useEffect(() => {
    if (searchData?.recs?.products?.length > 0) {
      setData(searchData);
      setSearchSchema(recs?.metadata?.apiName);
      setImpressionName(searchData?.recs?.metadata?.title);
    } else if (shouldRequestFallback()) {
      getMultiProductSkus();
    }
  }, [searchData]);

  useEffect(() => {
    if (!data && multiData?.recs?.products?.length > 0) {
      setData(multiData);
      setSearchSchema(multiData?.recs?.metadata?.apiName);
      setImpressionName(multiData?.recs?.metadata?.title);
    }
  }, [multiData]);

  // if component was rendered as a fallback then don't render it again
  if (didFallbackRender.fallbackRendered
    && didFallbackRender.zoneId !== zonePosition) {
    return null;
  }

  const wrapperClasses = className(
    'dynamic-recs',
    'sui-w-full',
    'sui-p-4',
    searchSchema,
    {
      loading: showLoading && loading
    }
  );

  if (multiError || (!loading && !multiLoading && !data
    && multiData !== undefined && !searchData?.recs && !multiData?.recs)) {
    return null;
  }
  const analyticsAnchorProduct = searchSchema === 'searchViewed' ? anchorId : itemIds?.split(',')[0];

  return (
    <ImpressionProvider
      data={{
        id: getDrecsImpressionID(impressionData),
        component: 'searchViewed',
        name: impressionContainerNameParser(impressionName || null),
        type: getDrecsimpressionType(impressionData),
        recType: searchSchema || '',
        position: '',
        category: '',
        categoryPosition: '',
        ...(name ? { sectionSlotID: name } : {}),
        ...(impressionData?.zonePosition ? { zonePosition: impressionData.zonePosition } : {}),
        ...(impressionData?.recType ? { recType: impressionData.recType } : {})
      }}
    >
      <div
        id={searchSchema}
        className={wrapperClasses}
        data-type="container"
        data-component="SearchViewedRecs"
      >
        <meta data-prop="name" content={searchSchema} />
        <ProductPodV7Carousel
          anchorImage={anchorImage}
          loading={isSearchLoading(data, searchData, multiData, loading, multiLoading)}
          hideFavorites={hideFavorites}
          itemClass="fixed-item"
          scheme={searchSchema}
          analyticsAnchorProductSku={analyticsAnchorProduct}
          membershipInformation={membershipInformation}
          storeId={storeId}
          zipCode={zipCode}
          maxItems={maxItems}
          showLoading={showLoading}
          data={data?.recs}
          parent={parent || 'plp-recs-placeholder'}
          zoneName={zonePosition ? impressionName : ''}
          zonePosition={zonePosition}
          podResults={data?.recs?.products?.length || multiData?.recs?.products?.length}
          telemetryKey={impressionContainerNameParser(impressionName || null)}
        />
      </div>
    </ImpressionProvider>
  );
};

SearchViewedRecs.propTypes = {
  anchorId: string.isRequired,
  anchorImage: string,
  hideFavorites: bool,
  requestKey: string,
  maxItems: number,
  showLoading: bool,
  itemIds: string,
  impressionData: shape({
    id: string,
    component: string,
    type: string
  }),
  name: string,
  parent: string,
  zonePosition: string
};

SearchViewedRecs.defaultProps = {
  anchorImage: null,
  hideFavorites: true,
  requestKey: null,
  showLoading: true,
  maxItems: 16,
  itemIds: '',
  impressionData: {
    id: null,
    component: null,
    type: null
  },
  name: '',
  parent: null,
  zonePosition: ''
};

SearchViewedRecs.dataModel = dynamicRecsDataModel;

SearchViewedRecs.displayName = 'SearchViewedRecs';

export default SearchViewedRecs;
